import {
  formatSettingUpdatedAt,
  getApiErrorMessage,
} from './economySettings';

export interface PlatformSettingDefinition {
  key: string;
  label: string;
  description: string;
  unit: string;
  min: number;
  max?: number;
  step: string;
}

/**
 * These are the platform controls that have an authoritative, audited
 * AdminSetting row and are consumed by the withdrawal paths on the backend.
 * The order is intentional: the global fee is followed by every supported
 * currency's minimum so an operator cannot mistake a local minimum for a
 * global amount.
 */
export const PLATFORM_SETTING_DEFINITIONS: PlatformSettingDefinition[] = [
  {
    key: 'withdrawal_service_fee_pct',
    label: 'Withdrawal service fee',
    description: 'Percentage deducted from each withdrawal before the net amount is paid out.',
    unit: '%',
    min: 0,
    max: 100,
    step: '0.1',
  },
  ...[
    ['NGN', 'Nigerian naira'],
    ['GHS', 'Ghanaian cedi'],
    ['GBP', 'British pound'],
    ['CAD', 'Canadian dollar'],
    ['USD', 'US dollar'],
    ['INR', 'Indian rupee'],
    ['AUD', 'Australian dollar'],
    ['EUR', 'euro'],
    ['KES', 'Kenyan shilling'],
    ['ZAR', 'South African rand'],
  ].map(([currency, name]) => ({
    key: `withdrawal_min_${currency.toLowerCase()}`,
    label: `Minimum withdrawal (${currency})`,
    description: `Smallest amount a user may withdraw in ${name}.`,
    unit: currency,
    min: 0,
    step: 'any',
  })),
];

/**
 * The old Platform tab displayed these values as local React state. They are
 * intentionally listed rather than rendered as editable inputs until the
 * backend has a real owner and runtime behavior for them.
 */
export const PLATFORM_UNAVAILABLE_DEFINITIONS = [
  {
    key: 'maintenance_mode',
    label: 'Maintenance mode',
    reason: 'No backend setting currently controls access to the platform.',
  },
  {
    key: 'registration_enabled',
    label: 'User registration',
    reason: 'Registration is not controlled by an AdminSetting yet.',
  },
  {
    key: 'withdrawal_enabled',
    label: 'Withdrawals enabled',
    reason: 'There is no supported global withdrawal kill switch yet.',
  },
  {
    key: 'email_notifications',
    label: 'Email notifications',
    reason: 'Notification delivery has no platform-level setting yet.',
  },
  {
    key: 'push_notifications',
    label: 'Push notifications',
    reason: 'Notification delivery has no platform-level setting yet.',
  },
  {
    key: 'max_daily_withdrawals',
    label: 'Maximum daily withdrawals',
    reason:
      'The backend enforces a per-country currency amount, not a global withdrawal count.',
  },
];

export const PLATFORM_SETTING_KEYS = new Set(
  PLATFORM_SETTING_DEFINITIONS.map(definition => definition.key)
);

const DECIMAL_PATTERN = /^[+]?(?:\d+(?:\.\d*)?|\.\d+)$/;

const canonicalizeDecimal = (rawValue: string): string => {
  const unsigned = rawValue.startsWith('+') ? rawValue.slice(1) : rawValue;
  const [rawIntegerPart, rawFractionPart = ''] = unsigned.split('.');
  const integerPart = (rawIntegerPart || '0').replace(/^0+/, '') || '0';
  const fractionPart = rawFractionPart.replace(/0+$/, '');
  return fractionPart ? `${integerPart}.${fractionPart}` : integerPart;
};

export interface ParsedPlatformSetting {
  value: string;
  canonicalValue: string;
}

export interface PlatformSettingParseResult {
  parsed?: ParsedPlatformSetting;
  error?: string;
}

/** Validate numeric browser input before it crosses the authenticated API seam. */
export const parsePlatformSettingValue = (
  definition: PlatformSettingDefinition,
  rawValue: string
): PlatformSettingParseResult => {
  const trimmed = rawValue.trim();
  if (trimmed === '') {
    return { error: `${definition.label} is required.` };
  }
  if (!DECIMAL_PATTERN.test(trimmed)) {
    return { error: `${definition.label} must be a finite decimal number.` };
  }

  const canonicalValue = canonicalizeDecimal(trimmed);
  const numericValue = Number(canonicalValue);
  if (!Number.isFinite(numericValue)) {
    return { error: `${definition.label} must be a finite decimal number.` };
  }
  if (numericValue < definition.min) {
    return { error: `${definition.label} must be at least ${definition.min}.` };
  }
  if (definition.max !== undefined && numericValue > definition.max) {
    return { error: `${definition.label} must be at most ${definition.max}.` };
  }

  return {
    parsed: {
      value: canonicalValue,
      canonicalValue,
    },
  };
};

export { formatSettingUpdatedAt, getApiErrorMessage };
